/**
 * The ground a battle is fought on — relief, water, wood and road.
 *
 * The complaint this file answers is a measured one, and `npm run battleground
 * -- --before` still prints the field as it was. Over five seeds of an open
 * field, the box the armies actually stand and fight in came out **80.4% one
 * terrain, three terrain kinds in all, median move cost 1.00, p90 1.46, and
 * 0.2 pieces of scatter** — the scatter being rolled uniformly across all
 * 147,456 tiles of a map a battle touches four thousand of. One piece of cover
 * per five battles. With the generator on it is 58.8% one terrain, 6.2 kinds,
 * p90 2.67, and the defender's line stands 0.59 tiles above the attacker's.
 *
 * (The `--before` figures are the field *with* the `Terrain.Deep` leak in
 * `flattenField` already stopped — see the note there. Measured before that
 * was fixed, the same box was 74.6% one terrain with **5.6% unannounced deep
 * water** on it that no scenario, muster or deployment zone had heard of.)
 *
 * Worse, and less obvious: **terrain has never slowed a man down.**
 * `TERRAIN_MOVE_COST` is read by `flow.ts` and `path.ts` and by nothing else,
 * so it changes which way a pathfinder routes and never how fast anybody
 * walks — and battle-mode companies do not path at all, they steer straight at
 * the enemy in their lane. Painting a marsh on a battlefield would therefore
 * have been decoration in the most literal sense. `groundSpeed` below is what
 * makes the paint mean something, and it is deliberately gated on there being
 * a height grid at all, so a settlement world's economy terrain is untouched
 * and the three siege digests in `tests/fields.test.ts` do not move.
 *
 * ## What is state and what is a cache
 *
 * Exactly the split `src/sim/obstacles.ts` established, and for the same
 * reasons:
 *
 *   **State.** `BattleState.hills` — a short list of rises, rolled from the
 *   battle's own seed, hashed inside the battle block and written to the save.
 *   Everything else the generator lays down goes into `map.terrain`, which has
 *   been hashed and saved since the map existed, so a river, a ford, a bridge,
 *   a wood and a road need no new state at all.
 *
 *   **A cache.** `World.heightGrid`, one byte a tile, a pure function of the
 *   hill list. Unhashed, unsaved, rebuilt by `rebuildHeightGrid` at
 *   `createBattle` and once on load. `null` in every world with no relief,
 *   which is every settlement in the game, so the hot readers pay one null
 *   check and nothing else.
 *
 * A whole map layer for height was considered and rejected on the argument
 * `obstacles.ts` already wrote down: `MAP_TILES` bytes in the hash and in every
 * save, for ever, for a thing that exists on a battlefield and nowhere else.
 */

import { MAP_H, MAP_TILES, MAP_W } from './constants.ts';
import { ObstacleKind, Terrain, TERRAIN_MOVE_COST, type FieldHill, type FieldObstacle } from './types.ts';
import { nextFloat, nextInt, type Rng } from '../core/rng.ts';
import { clamp, fbm2D, hash2 } from '../core/math.ts';
import { isNodeTerrain, nodeFootprintBounds } from './systems/nodes.ts';
import { NodeKind, nodeKind } from '../data/nodes.ts';
import type { World } from './world.ts';

/**
 * Grid units per tile of height.
 *
 * A `Uint8Array` and 32 units to the tile gives a ceiling of 7.97 tiles of
 * relief, which is about twice the tallest thing the generator below will ever
 * raise, and a quantum of 3 cm at the game's scale — far finer than anything
 * that reads it can tell apart.
 */
export const HEIGHT_UNITS = 32;

/** The tallest crest the generator will raise, in tiles. */
export const MAX_CREST = 4.2;

/**
 * How much of the pathfinder's cost is charged to a man's legs.
 *
 * Not all of it, and the number was measured rather than chosen.
 * `TERRAIN_MOVE_COST` is a **routing** table: `Deep` is 9 there so that a flow
 * field will go a very long way round rather than swim, which is the right
 * answer for a router and a catastrophic one for a pair of legs. Charged in
 * full, `npm run battleground -- --fight` went from **3 of 20 fields
 * undecided inside 9,000 ticks to 9 of 20** — companies too slow to close, too
 * slow to pursue and too slow to finish, and a battle that never ends is the
 * worst failure this mode has. Halving the bite put it back to 3.
 *
 * So: cost 1.45 (a wood) takes 18% off a man rather than 31%, and cost 9 takes
 * everything the floor below will allow. The router still sees the full table
 * and still goes round.
 */
const TERRAIN_BITE = 0.35;
/** How much of your speed a tile of climb costs. */
const CLIMB_COST = 0.3;
/** And what running downhill gives back. Deliberately much smaller. */
const DESCENT_GAIN = 0.14;
/**
 * No ground, however foul, more than halves a company.
 *
 * The floor exists because the numbers above multiply: a tired spear line
 * wading a river up a bank was on 0.21 of its own speed before this, which is
 * not slow, it is stopped. Half is a company that arrives late, strung out and
 * shot at the whole way — which is exactly the defensive value a river is
 * supposed to have and precisely what the `Deep` note in `types.ts` asks for.
 */
const MIN_GROUND_SPEED = 0.6;
/** Nor does any road make one fly. */
const MAX_GROUND_SPEED = 1.3;
/** Damage swing per tile of height advantage in a melee. */
const HIGH_GROUND_MELEE = 0.11;
/** And the most it can ever be worth, either way. */
const HIGH_GROUND_CAP = 1.6;
/** Tiles of extra reach per tile of height, for something that shoots. */
const HIGH_GROUND_RANGE = 0.8;
/** And the most of *that*. A knoll is not a tower. */
const HIGH_GROUND_RANGE_CAP = 3;

// ── The height cache ───────────────────────────────────────────────────────

/**
 * Rebuild the height bitmap from the hill list.
 *
 * Hills **max** rather than sum, which is what lets a ridge be built out of a
 * chain of overlapping discs without the overlaps stacking into a mountain.
 * Allocates only when there is relief to hold and drops the buffer when there
 * is not, so a settlement world never carries 147 KB it has no use for.
 */
export function rebuildHeightGrid(w: World, from?: readonly FieldHill[]): void {
  const hills = from ?? w.battle?.hills;
  if (!hills || hills.length === 0) {
    w.heightGrid = null;
    return;
  }
  const g = w.heightGrid && w.heightGrid.length === MAP_TILES ? w.heightGrid : new Uint8Array(MAP_TILES);
  g.fill(0);
  for (const hl of hills) {
    if (hl.r <= 0 || hl.h <= 0) continue;
    const x0 = Math.max(0, Math.floor(hl.x - hl.r));
    const x1 = Math.min(MAP_W - 1, Math.ceil(hl.x + hl.r));
    const y0 = Math.max(0, Math.floor(hl.y - hl.r));
    const y1 = Math.min(MAP_H - 1, Math.ceil(hl.y + hl.r));
    const inv = 1 / hl.r;
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        const dx = tx + 0.5 - hl.x;
        const dy = ty + 0.5 - hl.y;
        const s = 1 - Math.sqrt(dx * dx + dy * dy) * inv;
        if (s <= 0) continue;
        // Smoothstep, so a hill has a shoulder and a crest rather than a cone.
        const v = Math.round(hl.h * s * s * (3 - 2 * s) * HEIGHT_UNITS);
        const k = ty * MAP_W + tx;
        if (v > g[k]) g[k] = v > 255 ? 255 : v;
      }
    }
  }
  w.heightGrid = g;
}

/** How high the ground stands here, in tiles. Zero on any world with no relief. */
export function fieldHeight(w: World, x: number, y: number): number {
  const g = w.heightGrid;
  if (g === null) return 0;
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return 0;
  return g[ty * MAP_W + tx] / HEIGHT_UNITS;
}

/** The same, off a tile index. */
export function fieldHeightAt(w: World, k: number): number {
  const g = w.heightGrid;
  if (g === null || k < 0 || k >= MAP_TILES) return 0;
  return g[k] / HEIGHT_UNITS;
}

// ── What the ground does ───────────────────────────────────────────────────

/**
 * What this tile alone does to a man's speed, with no regard to slope.
 *
 * Split out of `groundSpeed` because the two questions are genuinely
 * different and mixing them produced the worst bug of this pass. See
 * `steerOverGround`: an army that scores its route on the *full* ground cost
 * refuses to attack uphill, because sliding sideways along the foot of a ridge
 * always scores better than climbing it. Which is not caution, it is a line
 * that never arrives.
 */
/**
 * The factor this ground puts on a man's speed, heading `(dx, dy)`.
 *
 * Two halves. The **terrain** half is the reciprocal of the cost the
 * pathfinder has always charged, so a road is quick, a marsh is a wade and the
 * middle of a river is very nearly a stop — one table, read by the router and
 * now by the legs, rather than two tables that would drift. The **slope** half
 * is measured a tile and a bit ahead, so a company charging a ridge pays for it
 * on the way up and gets a little of it back coming down.
 *
 * The **caller** decides whether the ground is charged at all: `stepMovement`
 * asks only when `w.heightGrid` is non-null, which is only on a battlefield.
 * This function does not check that itself, and deliberately — it is asked
 * directly by the harness and by tests about ground that has no relief on it
 * but is still a road or a marsh, and a silent `return 1` there would answer a
 * different question from the one asked.
 */
export function terrainSpeed(w: World, x: number, y: number): number {
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return 1;
  /**
   * Deliberately **not** floored, unlike `groundSpeed`.
   *
   * The floor exists to protect a pair of legs from a routing table; it has no
   * business blinding the eye that is choosing where to walk. Floored, the
   * middle of a channel and a muddy ford score the same 0.60 and an army has no
   * reason to prefer the crossing — which is the entire point of putting one
   * there. Unfloored, deep water scores 0.26 against the ford's 0.76 and the
   * column slides along the bank to the shallows, which is what it should do.
   */
  const f = 1 / (1 + ((TERRAIN_MOVE_COST[w.map.terrain[ty * MAP_W + tx]] ?? 1) - 1) * TERRAIN_BITE);
  return f > MAX_GROUND_SPEED ? MAX_GROUND_SPEED : f;
}

export function groundSpeed(w: World, x: number, y: number, dx: number, dy: number): number {
  const tx = Math.floor(x);
  const ty = Math.floor(y);
  if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return 1;
  let f = 1 / (1 + ((TERRAIN_MOVE_COST[w.map.terrain[ty * MAP_W + tx]] ?? 1) - 1) * TERRAIN_BITE);

  const g = w.heightGrid;
  if (g !== null) {
    const len = Math.hypot(dx, dy);
    if (len > 1e-4) {
      const ahead = 1.4 / len;
      const here = g[ty * MAP_W + tx];
      const nx = Math.floor(x + dx * ahead);
      const ny = Math.floor(y + dy * ahead);
      if (nx >= 0 && ny >= 0 && nx < MAP_W && ny < MAP_H) {
        const dh = (g[ny * MAP_W + nx] - here) / HEIGHT_UNITS;
        if (dh > 0) f /= 1 + CLIMB_COST * dh;
        else if (dh < 0) f *= 1 + DESCENT_GAIN * Math.min(1, -dh);
      }
    }
  }
  return f < MIN_GROUND_SPEED ? MIN_GROUND_SPEED : f > MAX_GROUND_SPEED ? MAX_GROUND_SPEED : f;
}

/**
 * What standing above your enemy is worth in a melee.
 *
 * `1` on any field with no relief, which is the cheap exit every settlement
 * world takes. Capped both ways: the high ground is an edge, not a win
 * condition, and a man fighting up a slope is at a disadvantage rather than
 * helpless.
 */
export function highGroundMelee(w: World, i: number, j: number): number {
  const g = w.heightGrid;
  if (g === null) return 1;
  const u = w.units;
  const dh = clamp(fieldHeight(w, u.x[i], u.y[i]) - fieldHeight(w, u.x[j], u.y[j]), -HIGH_GROUND_CAP, HIGH_GROUND_CAP);
  return 1 + dh * HIGH_GROUND_MELEE;
}

/** Extra reach from the ground under a shooter's feet. Zero without relief. */
export function highGroundRange(w: World, i: number): number {
  if (w.heightGrid === null) return 0;
  const h = fieldHeight(w, w.units.x[i], w.units.y[i]);
  const r = h * HIGH_GROUND_RANGE;
  return r > HIGH_GROUND_RANGE_CAP ? HIGH_GROUND_RANGE_CAP : r;
}

/**
 * Bend a heading around ground the mover would rather not cross.
 *
 * This is the half of the brief that is easy to skip and expensive to skip:
 * *ground the enemy cannot read is ground that only punishes the player.* An
 * uncommanded battle company marches in a straight line at whatever stands in
 * its lane (see `stepCompanies`), so a channel of deep water laid between the
 * armies would have drowned the machine and nobody else.
 *
 * Deliberately a local steer rather than a path. Three candidate headings —
 * straight on and thirty degrees either side — are scored over four samples
 * out to about ten tiles, on the same `groundSpeed` the legs pay, and the best
 * wins. That is nine grid reads per company per tick, for a couple of dozen
 * companies, against a Dijkstra per company which is what a real path would
 * cost; and it is enough for the behaviour that matters, which is a column
 * sliding along a river to the ford rather than wading in front of the archers.
 *
 * `climb` is how much this company *wants* height: positive for a missile line,
 * which will take a longer road to fight from a crest, zero for everyone else.
 * A charge does not go round a hill to avoid the climb — it goes round the
 * water.
 *
 * Writes into `out` and returns nothing, because it runs every tick for every
 * company and an allocation here is an allocation in the tick.
 */
const STEER_ANGLES = [0, 0.52, -0.52, 1.05, -1.05];
const STEER_SAMPLES = 4;
const STEER_REACH = 10;
/**
 * How bad the ground straight ahead has to be before the line will go round it.
 *
 * Without this gate the steer is a *preference* and it fires everywhere: a
 * company will take a thirty-degree detour to shave a wood's edge, its
 * neighbour will detour the other way, and an advancing line loses its
 * frontage. Measured, `tests/battle-lanes.test.ts` watched a fifty-seven-tile
 * enemy frontage narrow to twenty-one in thirty seconds — which is the mob
 * generator that test exists to catch, arrived at from a new direction.
 *
 * With it, the steer is a *last resort*: 0.94 is roughly "there is a marsh or
 * worse in front of you", so open ground, a road and even a wood are walked
 * straight through and only water and bog bend a column. Which is the whole
 * behaviour that was wanted — a line that slides along a bank to the ford —
 * without the one that was not.
 */
const STEER_GATE = 0.94;

export function steerOverGround(
  w: World,
  x: number,
  y: number,
  dx: number,
  dy: number,
  climb: number,
  out: { x: number; y: number },
): void {
  out.x = dx;
  out.y = dy;
  if (w.heightGrid === null) return;
  const base = Math.atan2(dy, dx);
  let bestScore = -Infinity;
  let bestA = 0;
  for (let n = 0; n < STEER_ANGLES.length; n++) {
    const a = base + STEER_ANGLES[n];
    const cx = Math.cos(a);
    const cy = Math.sin(a);
    let sum = 0;
    for (let s = 1; s <= STEER_SAMPLES; s++) {
      const d = (STEER_REACH * s) / STEER_SAMPLES;
      const px = x + cx * d;
      const py = y + cy * d;
      sum += terrainSpeed(w, px, py);
      if (climb > 0) sum += climb * fieldHeight(w, px, py);
    }
    /**
     * Turning aside is not free.
     *
     * Without this term a company would take a wide arc for a trivial gain and
     * two companies with the same lane would fan apart, which is the mob
     * generator `stepCompanies` already has a note about. The penalty is
     * proportional to how far off the wanted heading the candidate is, so the
     * straight line wins every tie and only genuinely bad ground moves it.
     */
    const score = sum / STEER_SAMPLES - Math.abs(STEER_ANGLES[n]) * 0.16;
    // Good enough straight ahead: hold the lane. See `STEER_GATE`.
    if (n === 0 && score >= STEER_GATE) return;
    if (score > bestScore) {
      bestScore = score;
      bestA = a;
    }
  }
  out.x = Math.cos(bestA);
  out.y = Math.sin(bestA);
}

// ── Generation ─────────────────────────────────────────────────────────────

/**
 * How a country bends the ground a battle is fought on.
 *
 * The same shape as `BIOME_MIX` in `map.ts` and deliberately a separate table:
 * that one is about what a *settlement* has to live off — where the timber and
 * the ore are — and this one is about what an *army* has to cross. A desert has
 * no wood and no water for either of them, but the reason a desert battle is a
 * desert battle is dunes and a dry wadi, which is not a fact `BIOME_MIX` has
 * any business knowing.
 *
 * `relief` scales both the number of rises and their height; `wet` is the
 * chance of a watercourse; `wood` scales the copses; `bog` the soft ground.
 */
interface GroundMix {
  relief: number;
  wet: number;
  wood: number;
  bog: number;
  /** Dunes rather than hills: many, low, wide. */
  dune: boolean;
}

const GROUND_MIX: Record<string, GroundMix> = {
  plain: { relief: 1, wet: 0.5, wood: 1, bog: 0.25, dune: false },
  forest: { relief: 0.9, wet: 0.5, wood: 2.2, bog: 0.3, dune: false },
  jungle: { relief: 1, wet: 0.7, wood: 2.4, bog: 0.5, dune: false },
  mountain: { relief: 2.1, wet: 0.35, wood: 0.5, bog: 0.05, dune: false },
  hill: { relief: 1.6, wet: 0.45, wood: 0.9, bog: 0.12, dune: false },
  desert: { relief: 1.1, wet: 0.12, wood: 0.05, bog: 0, dune: true },
  ice: { relief: 1.2, wet: 0.15, wood: 0.2, bog: 0, dune: false },
  marsh: { relief: 0.45, wet: 0.9, wood: 0.7, bog: 1.6, dune: false },
  coast: { relief: 0.7, wet: 0.7, wood: 0.6, bog: 0.4, dune: true },
  ruin: { relief: 0.9, wet: 0.4, wood: 0.5, bog: 0.2, dune: false },
};

export function groundMix(biome: string | undefined): GroundMix {
  return GROUND_MIX[biome ?? 'plain'] ?? GROUND_MIX.plain;
}

/** A rectangle nothing is laid inside. */
export interface Keep {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface GroundPlan {
  /** Middle of the field. */
  cx: number;
  cy: number;
  /** The defender's muster row, before the ground has had its say. */
  defenderY: number;
  /** The country this field stands in. `WorldConfig.biome`. */
  biome: string | undefined;
  /** The defender's town, if this is a siege: nothing is laid inside its skirt. */
  town: { x: number; y: number; r: number } | null;
}

export interface GroundReport {
  hills: FieldHill[];
  /** Scatter that belongs to a wood rather than to the open field. */
  woodScatter: FieldObstacle[];
  /** What was laid, for the harness and for the battle log. */
  ridges: number;
  copses: number;
  water: boolean;
  crossings: number;
  road: boolean;
  bogs: number;
  /** Resource nodes removed because the ground moved out from under them. */
  uprooted: number;
}

/** Half-width and half-height of the ground that gets a battlefield's attention. */
export const FIELD_HALF_W = 92;
export const FIELD_HALF_H = 66;

/**
 * Lay the ground, and then read it back to say where the defender stands.
 *
 * Order is the design. The relief goes down first, because everything else is
 * an answer to it — water runs in the low ground, bogs sit in hollows, the road
 * takes the easiest line, the defender takes the crest. Doing it the other way
 * round produces a river along a ridge top, which is the sort of thing that
 * makes a generated map read as generated.
 *
 * Everything but the hills is written straight into `map.terrain`, which has
 * been hashed and saved since the map existed. The only new state in the whole
 * feature is the hill list, and it is returned rather than stored so the caller
 * can put it where the hash already walks.
 */
export function layBattleGround(w: World, rng: Rng, plan: GroundPlan): GroundReport {
  const mix = groundMix(plan.biome);
  const woodland = plan.biome === 'forest' || plan.biome === 'jungle';
  const map = w.map;
  const x0 = Math.max(2, Math.round(plan.cx - FIELD_HALF_W));
  const x1 = Math.min(MAP_W - 3, Math.round(plan.cx + FIELD_HALF_W));
  const y0 = Math.max(2, Math.round(plan.cy - FIELD_HALF_H));
  const y1 = Math.min(MAP_H - 3, Math.round(plan.cy + FIELD_HALF_H));

  const report: GroundReport = {
    hills: [],
    woodScatter: [],
    ridges: 0,
    copses: 0,
    water: false,
    crossings: 0,
    road: false,
    bogs: 0,
    uprooted: 0,
  };

  /** Inside the walls, or close enough that a wall would stand in it. */
  const inTown = (x: number, y: number, skirt: number): boolean => {
    const t = plan.town;
    if (!t) return false;
    return Math.hypot(x - t.x, y - t.y) < t.r + skirt;
  };

  // ── 1. Relief ────────────────────────────────────────────────────────────
  /**
   * Where the high ground is, and whose it is.
   *
   * Two thirds of the time the main rise is on the **defender's** half, which
   * is the oldest rule in the subject: the side that stands still picks the
   * ground. The rest of the time it is in the middle or on the attacker's half,
   * and then the ridge is the objective rather than the position — which is the
   * other kind of battle worth having and the reason this is a roll and not a
   * constant.
   */
  const dSign = Math.sign(plan.defenderY - plan.cy) || 1;
  const ridgeCount = mix.dune ? 3 + nextInt(rng, 0, 3) : 1 + nextInt(rng, 0, Math.max(1, Math.round(mix.relief * 1.6)));
  for (let n = 0; n < ridgeCount; n++) {
    const defenders = n === 0 ? nextFloat(rng) < 0.66 : nextFloat(rng) < 0.5;
    const side = defenders ? dSign : -dSign;
    // The first rise lands near a muster row; the rest anywhere on the field.
    const ry =
      n === 0
        ? plan.cy + side * (11 + nextFloat(rng) * 17)
        : plan.cy + (nextFloat(rng) * 2 - 1) * FIELD_HALF_H * 0.7;
    // The first rise stays under the line; the rest may be anywhere.
    const rx = plan.cx + (nextFloat(rng) * 2 - 1) * (n === 0 ? 24 : 46);
    // A ridge is a chain of discs. Its axis is mostly across the line of
    // advance, so it is something to be climbed rather than walked along.
    const along = nextFloat(rng) < 0.72 ? 0 : Math.PI / 2;
    const tilt = along + (nextFloat(rng) * 2 - 1) * 0.55;
    const links = mix.dune ? 2 + nextInt(rng, 0, 3) : 2 + nextInt(rng, 0, 4);
    const crest = mix.dune
      ? (0.8 + nextFloat(rng) * 0.9) * mix.relief
      : clamp((1.8 + nextFloat(rng) * 2.2) * mix.relief, 1.1, MAX_CREST);
    const radius = mix.dune ? 16 + nextFloat(rng) * 12 : 10 + nextFloat(rng) * 9;
    for (let l = 0; l < links; l++) {
      const t = links === 1 ? 0 : l / (links - 1) - 0.5;
      const hx = rx + Math.cos(tilt) * t * radius * 2.1;
      const hy = ry + Math.sin(tilt) * t * radius * 2.1;
      if (inTown(hx, hy, 4)) continue;
      report.hills.push({
        x: hx,
        y: hy,
        r: radius * (0.8 + nextFloat(rng) * 0.4),
        // The ends of a ridge are lower than its middle.
        h: crest * (1 - Math.abs(t) * 0.5),
      });
    }
    report.ridges++;
  }

  // ── 2. Water ─────────────────────────────────────────────────────────────
  /**
   * A watercourse, and the one or two places an army can get over it.
   *
   * The channel runs across the line of advance so it is between the armies
   * rather than beside them, it is `Deep` in the middle — nine times the cost
   * to cross, which is a company arriving late, strung out and shot at the
   * whole way — and shallow `River` at the banks. The crossings are the entire
   * point: a ford is cheap ground in an expensive line, and every company on
   * the field, both sides, wants to be standing on it.
   */
  const wantWater = nextFloat(rng) < mix.wet;
  const chan: Array<{ x: number; y: number }> = [];
  if (wantWater) {
    const vertical = nextFloat(rng) < 0.28;
    const wander = 10 + nextFloat(rng) * 14;
    const phase = nextFloat(rng) * Math.PI * 2;
    const base = vertical
      ? plan.cx + (nextFloat(rng) * 2 - 1) * 30
      : plan.cy + (nextFloat(rng) * 2 - 1) * 14;
    const steps = 64;
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const drift = Math.sin(phase + t * 3.1) * wander;
      if (vertical) chan.push({ x: base + drift, y: y0 + t * (y1 - y0) });
      else chan.push({ x: x0 + t * (x1 - x0), y: base + drift });
    }
    const halfW = 2.2 + nextFloat(rng) * 2.6;

    /**
     * The crossings, chosen before the water is painted.
     *
     * One is a **ford** — shallow the whole way over, slow but free. The other,
     * when there is one, is a **bridge**: quick, narrow, and the tile everybody
     * fights for. Two at most, and at least twenty tiles apart, so the field
     * has a decision on it rather than a wall or a corridor.
     */
    const crossN = 1 + (nextFloat(rng) < 0.55 ? 1 : 0);
    const crossAt: Array<{ t: number; bridge: boolean }> = [];
    for (let n = 0; n < crossN && crossAt.length < 2; n++) {
      let t = 0.2 + nextFloat(rng) * 0.6;
      let tries = 0;
      while (crossAt.some((c) => Math.abs(c.t - t) < 0.22) && tries++ < 8) {
        t = 0.2 + nextFloat(rng) * 0.6;
      }
      crossAt.push({ t, bridge: n === 1 || nextFloat(rng) < 0.4 });
    }

    for (let s = 0; s < chan.length; s++) {
      const p = chan[s];
      const t = s / (chan.length - 1);
      const cross = crossAt.find((c) => Math.abs(c.t - t) < (c.bridge ? 0.022 : 0.045));
      const rr = Math.ceil(halfW) + 1;
      for (let dy = -rr; dy <= rr; dy++) {
        for (let dx = -rr; dx <= rr; dx++) {
          const tx = Math.round(p.x) + dx;
          const ty = Math.round(p.y) + dy;
          if (tx < x0 || ty < y0 || tx > x1 || ty > y1) continue;
          if (inTown(tx, ty, 3)) continue;
          const d = Math.hypot(tx + 0.5 - p.x, ty + 0.5 - p.y);
          if (d > halfW + 1) continue;
          const k = map.idx(tx, ty);
          if (cross) {
            /**
             * A ford is `Marsh`, and that is a mechanical choice rather than a
             * decorative one. There is no shallow-water terrain in this game —
             * `River` is already the channel here — so a ford painted `River`
             * would cost exactly what the water beside it costs and an army
             * would have no reason on earth to use it. `Marsh` is soft ground
             * you can get across: 1.9 to the pathfinder against the channel's
             * 9, and three quarters speed to a pair of legs against three
             * fifths. Slow, unpleasant, and much better than swimming, which is
             * what a ford is.
             */
            map.terrain[k] = cross.bridge && d <= halfW ? Terrain.Bridge : Terrain.Marsh;
          } else if (d <= halfW - 1.2) {
            map.terrain[k] = Terrain.Deep;
          } else if (map.terrain[k] !== Terrain.Deep) {
            map.terrain[k] = Terrain.River;
          }
        }
      }
    }
    report.water = true;
    report.crossings = crossAt.length;

    /**
     * The bed is dug as well as painted.
     *
     * A river that is only a colour is a river a company charges over without
     * noticing, because `groundSpeed` reads the terrain and the *slope* and the
     * slope is what tells a man he is going down a bank. Cutting the hills back
     * along the channel also stops the generator producing the one thing that
     * reads as generated above all others: water running along a ridge top.
     */
    for (const p of chan) {
      for (let n = report.hills.length - 1; n >= 0; n--) {
        const hl = report.hills[n];
        if (Math.hypot(hl.x - p.x, hl.y - p.y) < hl.r * 0.55) report.hills.splice(n, 1);
      }
    }
  }

  // ── 3. Wood ──────────────────────────────────────────────────────────────
  /**
   * Copses, and the trees that make them real.
   *
   * `Forest` terrain is half of a wood — it costs 45% more to cross and now
   * actually slows a man down. The other half is the scatter, which is what
   * refuses a foot, stops an arrow and hides the company behind it, and which
   * was previously rolled uniformly over the whole 384×384 map: an average of
   * **0.6 pieces inside the fight box**. Here it is rolled *inside the copse*,
   * so a wood on the picture is a wood in the simulation.
   */
  const copses = Math.round(1 + nextFloat(rng) * 1.5 + mix.wood * 1.3);
  for (let n = 0; n < copses; n++) {
    /**
     * On a flank, not across the middle.
     *
     * The first cut scattered copses anywhere on the field and covered **27.5%
     * of the fight box in wood**, most of it straight down the line of advance.
     * Fought out, that was the single most expensive thing in the pass: `npm
     * run battleground -- --fight --wide` went from 9 undecided fields in 40
     * with the woods off to 16 with them on, because an army that has to cross
     * a wood to reach the enemy crosses it at four fifths speed, in cover, out
     * of formation, and mostly does not arrive.
     *
     * A wood belongs where a wood is interesting — on a flank, where it breaks
     * a line, hides a turning movement and anchors an end of the field. So the
     * lateral offset has a floor: no copse lands within twenty tiles of the
     * centre line, which is the frontage the two armies actually meet on.
     */
    const side = nextFloat(rng) < 0.5 ? -1 : 1;
    const wx = plan.cx + side * (20 + nextFloat(rng) * (FIELD_HALF_W * 0.5));
    const wy = plan.cy + (nextFloat(rng) * 2 - 1) * (FIELD_HALF_H * 0.55);
    const wr = 6 + nextFloat(rng) * 9;
    if (inTown(wx, wy, 6)) continue;
    let painted = 0;
    for (let ty = Math.floor(wy - wr); ty <= Math.ceil(wy + wr); ty++) {
      for (let tx = Math.floor(wx - wr); tx <= Math.ceil(wx + wr); tx++) {
        if (tx < x0 || ty < y0 || tx > x1 || ty > y1) continue;
        const d = Math.hypot(tx + 0.5 - wx, ty + 0.5 - wy);
        // A ragged edge rather than a disc, from the low bit of the position.
        if (d > wr * (0.75 + ((tx * 7 + ty * 13) % 5) * 0.06)) continue;
        const k = map.idx(tx, ty);
        const t = map.terrain[k];
        if (t === Terrain.Deep || t === Terrain.River || t === Terrain.Bridge) continue;
        map.terrain[k] = Terrain.Forest;
        painted++;
      }
    }
    if (painted === 0) continue;
    report.copses++;
    const trees = Math.round(wr * 1.5);
    for (let s = 0; s < trees; s++) {
      const a = nextFloat(rng) * Math.PI * 2;
      const rr = Math.sqrt(nextFloat(rng)) * wr * 0.8;
      const ox = wx + Math.cos(a) * rr;
      const oy = wy + Math.sin(a) * rr;
      if (ox < 4 || oy < 4 || ox > MAP_W - 4 || oy > MAP_H - 4) continue;
      report.woodScatter.push({
        kind: nextFloat(rng) < 0.78 ? ObstacleKind.Tree : ObstacleKind.Thicket,
        x: ox,
        y: oy,
        r: 1.8 + nextFloat(rng) * 2.4,
      });
    }
  }

  // ── 4. Soft ground ───────────────────────────────────────────────────────
  const bogs = Math.round(nextFloat(rng) * mix.bog * 2.4);
  for (let n = 0; n < bogs; n++) {
    const bx = plan.cx + (nextFloat(rng) * 2 - 1) * (FIELD_HALF_W * 0.5);
    const by = plan.cy + (nextFloat(rng) * 2 - 1) * (FIELD_HALF_H * 0.6);
    const br = 5 + nextFloat(rng) * 9;
    if (inTown(bx, by, 5)) continue;
    for (let ty = Math.floor(by - br); ty <= Math.ceil(by + br); ty++) {
      for (let tx = Math.floor(bx - br); tx <= Math.ceil(bx + br); tx++) {
        if (tx < x0 || ty < y0 || tx > x1 || ty > y1) continue;
        if (Math.hypot(tx + 0.5 - bx, ty + 0.5 - by) > br) continue;
        const k = map.idx(tx, ty);
        if (map.terrain[k] !== Terrain.AshPlain) continue;
        map.terrain[k] = Terrain.Marsh;
      }
    }
    report.bogs++;
  }

  // ── 5. The road ──────────────────────────────────────────────────────────
  /**
   * One road, and it goes somewhere.
   *
   * At a siege it runs from the edge of the field to the town's southern gate,
   * which is the line an assaulting column will actually take; on open ground
   * it runs the length of the field past the crossing, which is what a road
   * does. It costs 0.68 to walk against the plain's 1, so a company that finds
   * it arrives a third sooner — the "a column arrives faster than it could
   * across the field" half of the brief, and the reason it is worth holding.
   */
  const roadPts: Array<{ x: number; y: number }> = [];
  if (plan.town) {
    const gy = plan.town.y + plan.town.r;
    roadPts.push({ x: plan.town.x, y: gy });
    roadPts.push({ x: plan.town.x + (nextFloat(rng) * 2 - 1) * 14, y: (gy + y1) / 2 });
    roadPts.push({ x: plan.town.x + (nextFloat(rng) * 2 - 1) * 26, y: y1 });
  } else if (nextFloat(rng) < 0.7) {
    const ry = plan.cy + (nextFloat(rng) * 2 - 1) * 22;
    // Through a crossing when there is one, so the road and the ford agree.
    const via = chan.length > 0 ? chan[Math.floor(chan.length * 0.5)] : { x: plan.cx, y: ry };
    roadPts.push({ x: x0, y: ry });
    roadPts.push({ x: via.x, y: (ry + via.y) / 2 });
    roadPts.push({ x: x1, y: ry + (nextFloat(rng) * 2 - 1) * 16 });
  }
  if (roadPts.length > 1) {
    for (let s = 0; s + 1 < roadPts.length; s++) {
      const a = roadPts[s];
      const b = roadPts[s + 1];
      const steps = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) * 2);
      for (let t = 0; t <= steps; t++) {
        const px = a.x + ((b.x - a.x) * t) / steps;
        const py = a.y + ((b.y - a.y) * t) / steps;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const tx = Math.round(px) + dx;
            const ty = Math.round(py) + dy;
            if (tx < x0 || ty < y0 || tx > x1 || ty > y1) continue;
            if (inTown(tx, ty, 1)) continue;
            const k = map.idx(tx, ty);
            const cur = map.terrain[k];
            // A road does not cross deep water; it fords, or it stops.
            if (cur === Terrain.Deep) continue;
            if (cur === Terrain.Bridge) continue;
            map.terrain[k] = Terrain.Road;
          }
        }
      }
    }
    report.road = true;
  }

  if (woodland) {
    // A forest battle has continuous wooded flanks, rather than a handful of
    // isolated discs. A clear centre and both muster bands leave room to fight.
    // This field uses the world seed directly and consumes no shared RNG draws.
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      if (inTown(x, y, 10)) continue;
      const k = map.idx(x, y), terrain = map.terrain[k];
      if (woodlandPassage(plan, x, y)) {
        if (terrain === Terrain.Forest) map.terrain[k] = Terrain.AshPlain;
      } else if (terrain === Terrain.AshPlain &&
        fbm2D(x / 60, y / 60, w.config.seed ^ 0x54a71, 2) > 0.3) {
        map.terrain[k] = Terrain.Forest;
      }
    }
    // Existing copse props must respect the same open corridors and roads.
    // Their complete footprint is later painted by paintObstacleTerrain.
    report.woodScatter = report.woodScatter.filter(o => {
      for (let y = Math.floor(o.y - o.r); y <= Math.ceil(o.y + o.r); y++) {
        for (let x = Math.floor(o.x - o.r); x <= Math.ceil(o.x + o.r); x++) {
          if ((x + 0.5 - o.x) ** 2 + (y + 0.5 - o.y) ** 2 > o.r ** 2) continue;
          if (!map.inBounds(x, y) || woodlandPassage(plan, x, y) || map.terrain[map.idx(x, y)] !== Terrain.Forest) return false;
        }
      }
      return true;
    });
  }

  /**
   * And the trees now standing in the river.
   *
   * A battlefield carries the settlement generator's whole resource store —
   * **5,133 nodes on a measured field** — which nothing on a battlefield ever
   * harvests and which the renderer nonetheless draws. Repainting the ground
   * under them would have produced precisely the fault `systems/nodes.ts` has a
   * paragraph about and the owner once reported: *"rocks and trees render in
   * the water"*.
   *
   * The rule is the one `removeNode` already states in the other direction — a
   * node belongs to the ground it grew from — so a node whose footprint is no
   * longer entirely node terrain is gone. Large mineral edges must not block a
   * new road or crossing just because their centre stayed dry. Killed rather than `removeNode`d, because
   * reverting the ground is exactly what must not happen here: the road, the
   * channel and the ford were put there on purpose, and a felled tree is not
   * entitled to undo them.
   */
  report.uprooted += pruneBattleResourceNodes(w, woodland);
  if (woodland) seedBattleWood(w, plan, x0, y0, x1, y1);
  map.invalidateRubbleIndex();

  map.terrainVersion++;
  w.dirty.flow = true;
  return report;
}

/** Run again after scatter painting, which can put a rock over existing trees. */
export function pruneBattleResourceNodes(w: World, exactKind = true): number {
  const nodes = w.nodes, map = w.map;
  let removed = 0;
  for (let i = 0; i < nodes.slots.high; i++) {
    if (!nodes.isAlive(i)) continue;
    const bounds = nodeFootprintBounds(nodes, i), expected = nodeKind(nodes.kind[i]).terrain;
    let rooted = true;
    for (let y = bounds.y0; y <= bounds.y1 && rooted; y++) for (let x = bounds.x0; x <= bounds.x1; x++) {
      const terrain = map.terrain[map.idx(x, y)];
      if (exactKind ? terrain !== expected : !isNodeTerrain(terrain)) { rooted = false; break; }
    }
    if (rooted) continue;
    nodes.kill(i); removed++;
  }
  return removed;
}

/** The nominal rows may move six tiles when the defender chooses high ground. */
function woodlandPassage(plan: GroundPlan, x: number, y: number): boolean {
  const dx = Math.abs(x - plan.cx);
  if (dx <= 18) return true;
  return dx <= 40 && (Math.abs(y - plan.defenderY) <= 14 || Math.abs(y - (2 * plan.cy - plan.defenderY)) <= 14);
}

/** Fill only newly wooded, empty lattice cells; existing trees keep their stores. */
function seedBattleWood(w: World, plan: GroundPlan, x0: number, y0: number, x1: number, y1: number): void {
  const def = nodeKind(NodeKind.Tree), size = def.lattice, cells = size * size;
  for (let by = Math.floor(y0 / size) * size; by <= y1; by += size) {
    for (let bx = Math.floor(x0 / size) * size; bx <= x1; bx += size) {
      let occupied = false;
      for (let dy = 0; dy < size && !occupied; dy++) for (let dx = 0; dx < size; dx++) {
        if (w.map.inBounds(bx + dx, by + dy) && w.nodes.at(w.map.idx(bx + dx, by + dy)) >= 0) { occupied = true; break; }
      }
      if (occupied) continue;
      const start = Math.floor(hash2(bx, by, 0x71aa3) * cells);
      for (let step = 0; step < cells; step++) {
        const at = (start + step) % cells, x = bx + at % size, y = by + Math.floor(at / size);
        if (x < x0 || y < y0 || x > x1 || y > y1 || woodlandPassage(plan, x, y)) continue;
        if (plan.town && Math.hypot(x - plan.town.x, y - plan.town.y) < plan.town.r + 10) continue;
        const k = w.map.idx(x, y);
        if (w.map.terrain[k] !== Terrain.Forest || w.map.wall[k] || w.map.building[k] >= 0 || w.map.rubble[k]) continue;
        w.nodes.spawn(k, NodeKind.Tree, def.amount);
        break;
      }
    }
  }
}

/**
 * Where the defender should actually stand, having seen the ground.
 *
 * The oldest rule in the subject, in eleven lines: walk the rows within six
 * tiles of the nominal muster line, score each by the mean height across the
 * frontage minus what the ground costs to stand on, and take the best. A
 * defender handed a ridge takes it; a defender whose only rise is a bog stays
 * where he was.
 *
 * Called after the relief exists and before anybody musters, so the whole army
 * — and the deployment rectangle the player is allowed to shuffle inside — is
 * placed by it.
 */
export function bestStand(w: World, cx: number, nominalY: number, span: number, reach = 6): number {
  let bestY = nominalY;
  let best = -Infinity;
  for (let dy = -reach; dy <= reach; dy++) {
    const y = nominalY + dy;
    if (y < 6 || y > MAP_H - 7) continue;
    let sum = 0;
    let n = 0;
    for (let x = cx - span; x <= cx + span; x += 3) {
      sum += fieldHeight(w, x, y) * 1.4 + terrainSpeed(w, x, y);
      n++;
    }
    // A defender does not walk far for a marginal rise: the nominal row wins
    // ties and near-ties, which keeps a flat field byte-identical to itself.
    const score = sum / Math.max(1, n) - Math.abs(dy) * 0.035;
    if (score > best) {
      best = score;
      bestY = y;
    }
  }
  return bestY;
}
